import { useState, type FormEvent } from "react";
import { Link, Redirect, useLocation } from "wouter";
import { useHistoryState } from "wouter/use-browser-location";
import { useAuth } from "../auth/AuthContext";
import { beginSSOLogin, isValidOrgSlug } from "../auth/ssoRoutes";
import { Button } from "../components/Button";
import "./login.css";

interface RedirectState {
  from?: string;
}

/**
 * The org-slug step of "Continue with SSO" (ADR-0037 §UX): the tenant-
 * qualified `/t/{tenant}/sso/login` route needs a tenant, and login is not
 * itself tenant-qualified (usernames are unique but not tenant-scoped in the
 * general case) — so an operator who arrives here cold (not via a
 * distributed deep link, which lands on `/t/{slug}/sso` directly and skips
 * this entirely, see `SSORedirectPage.tsx`) types their organization's slug
 * once. Submitting is a REAL browser navigation (`beginSSOLogin`), not a
 * client-side route change — there is nothing left for the SPA to render
 * after this until the IdP round trip completes.
 */
function SSOOrgSlugStep({ onBack }: { onBack: () => void }) {
  const [slug, setSlug] = useState("");
  const [touched, setTouched] = useState(false);
  const valid = isValidOrgSlug(slug);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setTouched(true);
    if (!valid) return;
    beginSSOLogin(slug);
  }

  return (
    <form className="login__form" onSubmit={handleSubmit} noValidate>
      <label className="login__field">
        <span>Organization slug</span>
        <input
          type="text"
          name="org-slug"
          autoComplete="organization"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          required
        />
        <span className="login__field-hint">
          The short name in your organization's Comebye sign-in link, e.g. "acme" from
          comebye.example.com/t/acme/sso.
        </span>
        {touched && !valid && (
          <span className="login__error" role="alert">
            Enter your organization's slug to continue.
          </span>
        )}
      </label>

      <Button type="submit" variant="primary" className="login__submit">
        Continue to sign-in
      </Button>
      <Button type="button" variant="ghost" className="login__submit" onClick={onBack}>
        Back
      </Button>
    </form>
  );
}

/**
 * Password sign-in, plus the real "Continue with SSO" entry point
 * (ADR-0037 §UX — this used to be a permanently-disabled placeholder). SSO
 * itself never happens on this page: clicking through takes the operator to
 * the org-slug step above, which hands off to a real browser navigation.
 */
export function LoginPage() {
  const { session, login, isAuthenticating, authError } = useAuth();
  const [, navigate] = useLocation();
  // ProtectedRoute stashes where the operator was headed on the History API
  // entry it redirected from; read it back so sign-in lands them there.
  const redirectState = useHistoryState<RedirectState | null>();
  // Default to "/" rather than a fleet page: the role is not known until the
  // session comes back, and the landing route resolves it (App.tsx's
  // RoleLanding) — a billing_admin must not be dropped on a fleet view it
  // cannot load.
  const destination = redirectState?.from ?? "/";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ssoStep, setSsoStep] = useState(false);

  if (session) {
    return <Redirect to={destination} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await login(username, password);
      navigate(destination, { replace: true });
    } catch {
      // authError from context already carries the operator-facing message.
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <span className="login__brand-mark" aria-hidden="true" />
          <span className="login__brand-name">Comebye</span>
        </div>
        <p className="login__subtitle">
          {ssoStep ? "Sign in with your organization's identity provider" : "Sign in to your control plane"}
        </p>

        {ssoStep ? (
          <SSOOrgSlugStep onBack={() => setSsoStep(false)} />
        ) : (
          <>
            <form className="login__form" onSubmit={(e) => void handleSubmit(e)} noValidate>
              {authError && (
                <p className="login__error" role="alert">
                  {authError}
                </p>
              )}

              <label className="login__field">
                <span>Username</span>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                />
              </label>

              <label className="login__field">
                <span>Password</span>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>

              <Button type="submit" variant="primary" disabled={isAuthenticating} className="login__submit">
                {isAuthenticating ? "Signing in…" : "Sign in"}
              </Button>
            </form>

            <div className="login__divider">
              <span>or</span>
            </div>

            <Button variant="secondary" className="login__sso" onClick={() => setSsoStep(true)}>
              Continue with SSO
            </Button>

            <p className="login__note">
              Don&apos;t have an account? <Link to="/register">Create one</Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
